import React, { useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import axios from 'axios'
import Container from '@parkholidays/legacy_components/dist/container'
import Typography from '@parkholidays/legacy_components/dist/typography'
import Card from '@parkholidays/legacy_components/dist/card'
import Button from '@parkholidays/legacy_components/dist/button'
import { useStyletron } from '@parkholidays/legacy_components'
import withCheckoutLayout from '../../layouts/withCheckoutLayout'
import CheckoutHeader from '../../components/CheckoutHeader'
import { datadogLogger } from '../../datadogLogger/logger'
import { classifyCspBlockedURI } from '../../utils/classifyCspBlockedURI'
import { paymentClientPrd, paymentClientStaging } from '../../config/urls/index'
import { generateId } from '../../utils/generateId'

const propTypes = {
  selectedProduct: PropTypes.shape({
    duration: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }),
  parkCode: PropTypes.string,
  onHandlePaymentSuccess: PropTypes.func.isRequired,
  isProduction: PropTypes.bool,
}

const ua = navigator.userAgent
const isIOS = /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && !window.safari && !/Chrome|Firefox|Chromium|Edge/i.test(ua))
const isAndroid = /Android/i.test(ua)
const applePaySessionExists = typeof window.ApplePaySession !== 'undefined'
const canMakePayments = isIOS && applePaySessionExists && window.ApplePaySession.canMakePayments()
const supportsPaymentRequest = typeof window.PaymentRequest !== 'undefined'
const isLikelyCaptiveIOS = isIOS && !ua.includes('Safari/') && !ua.includes('Version/')
const isLikelyAndroidWebView = isAndroid && ua.includes('Version/4.0')
const shift4Src = 'https://dev.shift4.com/checkout.js'


const Payment = ({ selectedProduct, parkCode, onHandlePaymentSuccess, isProduction }) => {
  const [, theme] = useStyletron()
  const [isLoading, setIsLoading] = useState(true)
  const [isAuthenticating, setIsAuthenticating] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [paymentError, setPaymentError] = useState(null)
  const isLoadingRef = useRef(isLoading)
  const isAuthenticatingRef = useRef(isAuthenticating)
  const isProcessingRef = useRef(isProcessing)
  const onHandlePaymentSuccessRef = useRef(onHandlePaymentSuccess)
  // const paymentAttemptInProgressRef = useRef(false)
  useEffect(() => { isLoadingRef.current = isLoading }, [isLoading])
  useEffect(() => { isAuthenticatingRef.current = isAuthenticating }, [isAuthenticating])
  useEffect(() => { isProcessingRef.current = isProcessing }, [isProcessing])
  useEffect(() => { onHandlePaymentSuccessRef.current = onHandlePaymentSuccess }, [onHandlePaymentSuccess])

  const { price } = selectedProduct
  const paymentClient = isProduction ? paymentClientPrd : paymentClientStaging

  const getStage = () => isProcessingRef.current
    ? 'voucher-generation'
    : isAuthenticatingRef.current
    ? 'checkout-open'
    : isLoadingRef.current
    ? 'payment-setup'
    : 'idle'

  useEffect(() => {
    const handleVisibilityChange = () => {
      datadogLogger.info(`payment: visibility-${document.visibilityState} stage-${getStage()} parkCode-${parkCode}`)
    }
    const handlePageHide = () => {
      datadogLogger.info(`payment: pagehide event stage-${getStage()} parkCode-${parkCode}`)
    }
    const handleCspViolation = (e) => {
      const blockedURICategory = classifyCspBlockedURI(e.blockedURI)
      datadogLogger.warn('payment: csp-violation', {
        parkCode,
        blockedURI: e.blockedURI,
        blockedURICategory,
        errorCategory: 'CSP_BLOCKED',
        effectiveDirective: e.effectiveDirective,
        violatedDirective: e.violatedDirective,
        disposition: e.disposition,
        documentURI: e.documentURI,
        sourceFile: e.sourceFile,
        originalPolicy: e.originalPolicy?.substring(0, 300),
        statusCode: e.statusCode,
        timestamp: new Date().toISOString(),
      })
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('pagehide', handlePageHide)
    document.addEventListener('securitypolicyviolation', handleCspViolation)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('pagehide', handlePageHide)
      document.removeEventListener('securitypolicyviolation', handleCspViolation)
    }
  }, [parkCode])

  useEffect(() => {
    datadogLogger.info(`payment: platform-${isIOS ? 'ios' : isAndroid ? 'android' : 'other'} likelyCaptiveIOS-${isLikelyCaptiveIOS} likelyAndroidWebView-${isLikelyAndroidWebView} applePaySessionExists-${applePaySessionExists} canMakePayments-${canMakePayments} supportsPaymentRequest-${supportsPaymentRequest} referrer-${document.referrer} parkCode-${parkCode}`)

    if (window.Shift4Checkout) {
      datadogLogger.info('payment: Shift4Checkout already ready')
      setIsLoading(false)
      return
    }

    const MAX_WAIT_MS = 10_000
    const POLL_INTERVAL_MS = 200
    const RETRY_DELAY_MS = 2000
    let poll = null
    let retryTimer = null
    let hasRetried = false

    const retryShift4Load = (reason) => {
      if (hasRetried) return
      hasRetried = true
      clearInterval(poll)
      datadogLogger.warn('payment: Shift4Checkout failed to load - retrying', {
        parkCode,
        reason,
        online: navigator.onLine,
        visibilityState: document.visibilityState,
        isLikelyCaptiveIOS,
        isLikelyAndroidWebView,
      })
      const oldScript = document.querySelector(`script[src="${shift4Src}"]`)
      if (oldScript) oldScript.remove()
      retryTimer = setTimeout(() => { attachScript(); startPolling() }, RETRY_DELAY_MS)
    }

    const attachScript = () => {
      if (document.querySelector(`script[src="${shift4Src}"]`)) return
      const script = document.createElement('script')
      script.src = shift4Src
      script.onload = () => datadogLogger.info(`payment: Shift4Checkout script loaded parkCode-${parkCode}`)
      script.onerror = (e) => {
        datadogLogger.error('payment: Shift4Checkout script failed to fetch', {
          parkCode,
          online: navigator.onLine,
          visibilityState: document.visibilityState,
          error: e?.message || e?.type || 'unknown',
        })
        retryShift4Load('onerror')
      }
      document.body.appendChild(script)
      datadogLogger.info(`payment: loading Shift4Checkout script${hasRetried ? ' (retry)' : ''}`)
    }

    const startPolling = () => {
      let elapsed = 0
      poll = setInterval(() => {
        if (window.Shift4Checkout) {
          clearInterval(poll)
          datadogLogger.info(`payment: Shift4Checkout ready after ${elapsed}ms${hasRetried ? ' (retry)' : ''} parkCode-${parkCode}`)
          setIsLoading(false)
          return
        }
        elapsed += POLL_INTERVAL_MS
        if (elapsed < MAX_WAIT_MS) return
        clearInterval(poll)

        if (hasRetried) {
          datadogLogger.error(`payment: Shift4Checkout failed to load after ${MAX_WAIT_MS}ms`, {
            parkCode,
            stage: getStage(),
            userAgent: navigator.userAgent,
            visibilityState: document.visibilityState,
            isLikelyCaptiveIOS,
            isLikelyAndroidWebView,
            applePaySessionExists,
            canMakePayments,
            supportsPaymentRequest,
          })
          setIsLoading(false)
          setPaymentError('We were unable to load the payment system. Please refresh the page or contact Park Reception.')
          return
        }
        retryShift4Load('poll-timeout')
      }, POLL_INTERVAL_MS)
    }

    attachScript()
    startPolling()

    return () => { clearInterval(poll); clearTimeout(retryTimer) }
  }, [parkCode])

  const handlePayment = async () => {
    // if (paymentAttemptInProgressRef.current) {
    //   datadogLogger.warn('payment: duplicate attempt blocked', {
    //     parkCode,
    //     stage: getStage(),
    //     isLoading: isLoadingRef.current,
    //     isAuthenticating: isAuthenticatingRef.current,
    //     isProcessing: isProcessingRef.current,
    //     visibilityState: document.visibilityState,
    //   })
    //   return
    // }
    // paymentAttemptInProgressRef.current = true

    if (!window.Shift4Checkout) {
      // paymentAttemptInProgressRef.current = false
      datadogLogger.error(`payment: Shift4Checkout not available parkCode-${parkCode}`)
      setPaymentError('Payment system not loaded. Please refresh the page.')
      return
    }

    setPaymentError(null)
    setIsAuthenticating(true)

    const bookingRef = `WIFI_${parkCode}_${generateId()}`
    const amount = Number(price).toFixed(2).replace('.', '')

    datadogLogger.info(`payment: authentication request parkCode-${parkCode} reference-${bookingRef}`)

    const authStartMs = Date.now()

    const graphqlQuery = {
      query: `query PaymentClient($currency: String!, $booking_no: String!, $amount: String!) {
        wifi_payment_client(currency: $currency, booking_no: $booking_no, amount: $amount) {
          clientSecret
          key
        }
      }`,
      variables: { currency: 'GBP', booking_no: bookingRef, amount },
    }

    try {
      const response = await axios({
        url: paymentClient,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(graphqlQuery),
      })

      const shift4Data = response.data?.data?.wifi_payment_client
      if (!shift4Data?.clientSecret || !shift4Data?.key) {
        // paymentAttemptInProgressRef.current = false
        datadogLogger.error(`payment: null response from wifi_payment_client parkCode-${parkCode} reference-${bookingRef}`)
        setIsAuthenticating(false)
        setPaymentError('We were unable to set up payment. Please refresh the page or contact Park Reception.')
        return
      }

      const shift4Script = document.querySelector(`script[src="${shift4Src}"]`)
      datadogLogger.info('payment: opening Shift4 checkout', {
        parkCode,
        documentReadyState: document.readyState,
        visibilityState: document.visibilityState,
        shift4CheckoutExists: !!window.Shift4Checkout,
        shift4CheckoutOpenExists: typeof window.Shift4Checkout?.open === 'function',
        scriptExists: !!shift4Script,
        scriptIsConnected: shift4Script?.isConnected ?? false,
        scriptParentExists: !!shift4Script?.parentNode,
      })

      window.Shift4Checkout.key = shift4Data.key

      let dismissPoller = null

      const forceCloseCheckoutAfterSuccess = () => {
        try {
          const checkoutFrame = document.querySelector('iframe[name="__privateSecurionPayCheckout"]')
          if (checkoutFrame) {
            let checkoutContainer = checkoutFrame
            while (checkoutContainer.parentElement && checkoutContainer.parentElement !== document.body) {
              checkoutContainer = checkoutContainer.parentElement
            }
            checkoutContainer.remove()
          }
        } catch (e) {
          datadogLogger.info(`payment: could not auto-close checkout after success - ${e?.message}`)
        }
      }

      window.Shift4Checkout.success = async (result) => {
        if (dismissPoller) { clearInterval(dismissPoller); dismissPoller = null }
        setTimeout(forceCloseCheckoutAfterSuccess, 2000)
        const chargeId = result?.charge?.id || 'unknown'
        const customerEmail = result?.email || null
        datadogLogger.info(`payment: transaction successful chargeId-${chargeId} emailPresent-${!!customerEmail} parkCode-${parkCode}`)
        setIsAuthenticating(false)
        setIsProcessing(true)
        try {
          await onHandlePaymentSuccessRef.current(chargeId, customerEmail, {
            paymentReference: bookingRef,
            amount: price,
            currency: 'GBP',
          })
        } catch (e) {
          setIsProcessing(false)
          setPaymentError(
            e?.message === 'Failed to fetch'
              ? 'Payment was successful but we were unable to generate your Wi-Fi voucher. Please contact Park Reception.'
              : e?.message || 'Payment was successful but we were unable to complete your Wi-Fi setup. Please contact Park Reception.'
          )
          datadogLogger.error(`payment: onHandlePaymentSuccess failed - ${e?.message || e}`)
        }
      }

      window.Shift4Checkout.error = (err) => {
        if (dismissPoller) {
          clearInterval(dismissPoller)
          dismissPoller = null
        }
        // paymentAttemptInProgressRef.current = false
        setIsAuthenticating(false)

        const errorMessage =
          typeof err === "string"
            ? err
            : err?.message ?? "Unknown"
              
        datadogLogger.error(
          `payment: Shift4 checkout error error-${errorMessage} parkCode-${parkCode} online-${navigator.onLine} visibility-${document.visibilityState}`
        )

        if (err?.message || err?.type) {
          setPaymentError(
            "Payment was not successful. Please try again or contact Park Reception."
          )
        } else {
          setPaymentError(null)
        }
      }

      window.Shift4Checkout.open({
        clientSecret: shift4Data.clientSecret,
        image: 'https://eu-images.contentstack.com/v3/assets/blt321e8804c3801f0c/bltc6b5c9865c7d1524/68da8f42615dc3acdd62e51d/PH-PL-mini-logos.jpg',
        name: 'Wi-Fi Payment',
        description: 'Park Holidays & Park Leisure',
      })

      // Shift4 hides the modal via CSS visibility on the live key, not DOM removal,
      // so MutationObserver never fires. Poll the main checkout iframe by name instead.
      let checkoutFrameHasAppeared = false
      dismissPoller = setInterval(() => {
        if (!isAuthenticatingRef.current || isProcessingRef.current) {
          clearInterval(dismissPoller); dismissPoller = null; return
        }
        const frame = document.querySelector('iframe[name="__privateSecurionPayCheckout"]')
        const isVisible = !!frame && frame.offsetWidth > 1 && frame.offsetHeight > 1
        if (isVisible) {
          if (!checkoutFrameHasAppeared) {
            datadogLogger.info('payment: Shift4 iframe visible', { parkCode, stage: getStage() })
          }
          checkoutFrameHasAppeared = true
          return
        }
        if (!checkoutFrameHasAppeared) return
        clearInterval(dismissPoller); dismissPoller = null
        // paymentAttemptInProgressRef.current = false
        datadogLogger.info(`payment: checkout dismissed by user frameExists-${!!frame} width-${frame?.offsetWidth ?? 'n/a'} height-${frame?.offsetHeight ?? 'n/a'} parkCode-${parkCode}`)
        setIsAuthenticating(false)
        setPaymentError(null)
      }, 300)
    } catch (error) {
      // paymentAttemptInProgressRef.current = false
      const paymentErrorCategory = error.response
        ? error.response.status >= 500 ? 'PAYMENT_HTTP_5XX' : 'PAYMENT_HTTP_4XX'
        : error.code === 'ECONNABORTED' ? 'PAYMENT_TIMEOUT'
        : error.request ? 'PAYMENT_STATUS_0'
        : 'PAYMENT_NETWORK_ERROR'
      datadogLogger.error(
        error.response ? 'payment: authorization failed' : 'payment: authorization network failure',
        {
          parkCode,
          errorCategory: paymentErrorCategory,
          elapsedMs: Date.now() - authStartMs,
          stage: getStage(),
          message: error.message,
          code: error.code,
          url: error.config?.url,
          method: error.config?.method,
          status: error.response?.status,
          response: error.response?.data,
          visibilityState: document.visibilityState,
          pathname: window.location.pathname,
        }
      )
      setIsAuthenticating(false)
      setPaymentError('We were unable to set up payment. Please refresh the page or contact Park Reception.')
    }
  }

  const showSpinner = isLoading || isAuthenticating || isProcessing
  const spinnerMessage = isProcessing ? 'Generating the Wi-Fi Voucher...' : 'Setting up payment...'

  return (
    <>
      <CheckoutHeader>
        <Container>
          <Typography as="h1" noMargins>
            Payment
          </Typography>
          <Typography as="p" noMargins>
            Please click the button below to complete your payment.
          </Typography>
        </Container>
      </CheckoutHeader>
      <Container>
        <Card color="white" elevation={3} style={{ padding: '1rem' }}>
          {showSpinner && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '1rem 0' }}>
              <div style={{
                width: '40px',
                height: '40px',
                border: `4px solid ${theme.palette.corporatePale}`,
                borderTopColor: theme.palette.corporate,
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }} />
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              <Typography as="p" align="center" noMargins style={{ marginTop: '1rem' }}>
                {spinnerMessage}
              </Typography>
            </div>
          )}
          {!showSpinner && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <style>{`
                .payment-btn-wrapper { width: 100%; }
                @media (min-width: 769px) { .payment-btn-wrapper { width: 50%; margin: 0 auto; padding-top: 2rem; } }
              `}</style>
              {paymentError && (
                <Typography as="p" noMargins style={{ color: theme.palette.error }}>
                  {paymentError}
                </Typography>
              )}
              <div className="payment-btn-wrapper">
                <Button color="corporate" onClick={handlePayment} fullWidth>
                  Pay Now £{Number(price).toFixed(2)}
                </Button>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '4px', padding: '4px 0' }}>
                <span style={{ fontSize: '12px', color: '#6b7280' }}>Powered by</span>
                <a href="https://shift4.com/" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center' }}>
                  <img src="https://js.dev.shift4.com/checkout-150f4459/images/powered-by.svg" height="14" alt="Shift4" />
                </a>
              </div>
            </div>
          )}
        </Card>
      </Container>
    </>
  )
}

Payment.propTypes = propTypes

export default withCheckoutLayout(Payment)
